import React from "react";
import clsx from "classnames";
import { Helmet } from "react-helmet";

import makeStyles from "@material-ui/core/styles/makeStyles";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";

import { Player, BigPlayButton, ControlBar, LoadingSpinner } from "video-react";
import "video-react/dist/video-react.css"; // or import scss

import Parallax from "components/Parallax";
import GridContainer from "components/GridContainer";
import GridItem from "components/GridItem";

import Why from "./sections/Why";
import Features from "./sections/Features";
import CallToAction from "components/CallToAction";
import Pricing from "./sections/Pricing";
// import FAQ from "./sections/FAQ";

import { container, title } from "assets/jss/material-kit-pro-react.jsx";

import { appUrl, rootUrl } from "../../../config";

const pathMedia = `${appUrl}/media/optaplan`;

const useStyles = makeStyles(theme => ({
  container: {
    ...container,
    zIndex: "12",
    color: "#FFFFFF",
  },
  main: {
    background: "#FFFFFF",
    position: "relative",
    zIndex: "3",
  },
  parallax: {
    background: "#FFFFFF",
    display: "flex",
    alignItems: "center",
  },
  appBarSpacer: theme.mixins.toolbar,
  content: {
    [theme.breakpoints.down("sm")]: {
      margin: "1em 0",
    },
  },
  title: {
    ...title,
    display: "block",
    position: "relative",
    marginTop: "30px",
    minHeight: "32px",
    textDecoration: "none",
    fontSize: "1.8em",
    [theme.breakpoints.down("sm")]: {
      lineHeight: "42px",
    },
    [theme.breakpoints.up("sm")]: {
      lineHeight: "72px",
    },
    [theme.breakpoints.up("md")]: {
      lineHeight: "48px",
      fontWeight: 500,
    },
    [theme.breakpoints.up("lg")]: {
      fontSize: "2.4em",
      lineHeight: "48px",
      fontWeight: 500,
    },
  },
  subtitle: {
    margin: "1.5rem 0",
  },
  product: {
    fontWeight: "normal",
    marginBottom: "1.5rem",
  },
  image: {
    width: "100%",
    height: "auto",
    borderRadius: 10,
  },
  button: {
    letterSpacing: 1,
    padding: "0.6rem 1rem",
    margin: "0.5em 0.5em 0.5em 0",
    fontSize: 12,
  },
  buttonFilled: {
    color: "#FFFFFF",
    backgroundColor: "#7446C4",
    "&:hover,&:focus": {
      color: "white",
      backgroundColor: "#916DD0",
    },
  },
  buttonOutlined: {
    color: "#7446C4",
    "&:hover,&:focus": {
      color: "#916DD0",
      backgroundColor: "rgba(250, 250, 250, 1)",
    },
  },
  player: {
    backgroundColor: "white !important",
  },
  videoContainer: {
    borderRadius: 10,
    boxShadow: "8px 8px 16px 0px rgba(0, 0, 0, 0.2), -8px 8px 16px 0px rgba(0, 0, 0, 0.2)",
  },
}));

const OptaPlan = props => {
  React.useEffect(() => {
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
  }, []);

  const classes = useStyles();

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>OptaPlan - TNA Software | Jaza Software</title>
        <link rel="canonical" href={`${rootUrl}/optaplan`} />
        <meta
          name="description"
          content="Say bye to spreadsheets & redundant ERP TNA. We bring you a perfect tool to manage Time and Action Calendar digitally."
        />
      </Helmet>

      <Parallax className={classes.parallax}>
        <div className={classes.container}>
          <GridContainer alignItems="center" justify="space-between" spacing={2}>
            <GridItem xs={12} sm={5} md={5} className={classes.content}>
              <div>
                <Typography variant="h5" className={classes.product}>
                  &gt; OptaPlan
                </Typography>
              </div>
              <div>
                <Typography variant="h4">Best TNA Software to keep your orders on track</Typography>
              </div>
              <div className={classes.subtitle}>
                <Typography variant="subtitle1">
                  Say bye to spreadsheets & redundant ERP TNA. We bring you a perfect tool to manage Time and Action
                  Calendar digitally.
                </Typography>
              </div>
              <div style={{ marginTop: 32 }}>
                <Button
                  variant="contained"
                  className={clsx(classes.button, classes.buttonFilled)}
                  onClick={_ => props.history.push("/demo", { products: "OptaPlan" })}>
                  SCHEDULE A DEMO
                </Button>
                <Button
                  component="a"
                  href="#why"
                  variant="outlined"
                  className={clsx(classes.button, classes.buttonOutlined)}
                  color="primary">
                  LEARN MORE
                </Button>
              </div>
            </GridItem>
            <GridItem xs={12} sm={6} md={6}>
              <Paper className={classes.videoContainer}>
                <Player
                  playsInline
                  src={`${pathMedia}/optaplan-min.mp4`}
                  fluid={true}
                  poster={`${pathMedia}/optaplan-poster.png`}>
                  <ControlBar autoHide={true} className="my-class" />
                  <LoadingSpinner />
                  <BigPlayButton position="center" />
                </Player>
              </Paper>
            </GridItem>
          </GridContainer>
        </div>
      </Parallax>

      <div className={classes.main}>
        <Why id="why" />
        <Features id="features" />
        <Pricing history={props.history} />
        <CallToAction
          title="Take control of orders with OptaPlan"
          subtitle="TNA management made delightfully simple"
          actionLabel="Request a Free Demo"
          color="purple"
          height={320}
          onClick={_ => props.history.push("/demo", { products: "OptaPlan" })}
        />
      </div>
    </div>
  );
};

export default OptaPlan;
